import { defineStore } from 'pinia';
import { ptAssetLogList } from '@/api/wallet';

function normalizeText(value) {
  if (value === null || value === undefined) {
    return '';
  }
  const text = String(value).trim();
  if (!text || text.toLowerCase() === 'null' || text.toLowerCase() === 'undefined') {
    return '';
  }
  return text;
}

function pickFirstValue(record, keys = []) {
  for (const key of keys) {
    const value = normalizeText(record?.[key]);
    if (value) {
      return value;
    }
  }
  return '';
}

function resolveStatusKey(record = {}) {
  const raw = String(
    record?.status
    ?? record?.receiveStatus
    ?? record?.rewardStatus
    ?? record?.claimStatus
    ?? ''
  ).trim().toLowerCase();

  if (!raw) {
    return '';
  }

  if (['available', 'withdrawable', 'claimable'].includes(raw)) {
    return 'withdrawable';
  }
  if (['0', 'pending', 'wait', 'unclaimed', 'todo', 'settling'].includes(raw)) {
    return 'pending';
  }
  if (['1', 'claimed', 'received', 'done', 'success', 'settled'].includes(raw)) {
    return 'received';
  }
  if (['2', 'deducted', 'reversed', 'refunded', 'expired', 'timeout', 'invalid'].includes(raw)) {
    return 'deducted';
  }
  return '';
}

function resolveTypeKey(record = {}, title = '') {
  const raw = String(
    record?.commissionType
    ?? record?.rewardType
    ?? record?.bizType
    ?? record?.businessType
    ?? record?.logType
    ?? record?.scene
    ?? record?.source
    ?? ''
  ).trim().toLowerCase();
  const searchable = `${raw} ${title}`.toLowerCase();

  if (raw === '1') {
    return 'purchase';
  }
  if (raw === '2') {
    return 'consumption';
  }
  if (/(开卡|购卡|open.?card|card.?open|purchase)/i.test(searchable)) {
    return 'purchase';
  }
  if (/(消费|交易|spend|consumption|payment|trade)/i.test(searchable)) {
    return 'consumption';
  }
  return '';
}

export function normalizeInviteRewardRecord(record = {}) {
  const addOrSubAmount = pickFirstValue(record, ['addOrSubAmount', 'changeAmount', 'amountChange']) || '0';
  const title = pickFirstValue(record, ['logLevel', 'logLevelMsg', 'remark', 'detail']) || '--';
  return {
    ...record,
    id: pickFirstValue(record, ['id', 'logId', 'recordId']) || `${record?.createTime || ''}-${addOrSubAmount}`,
    title,
    amount: addOrSubAmount,
    amountAfter: pickFirstValue(record, ['amountAfter', 'balanceAfter', 'afterAmount']) || '0',
    createTime: pickFirstValue(record, ['createTime', 'time', 'createTimeStr']),
    statusKey: resolveStatusKey(record),
    typeKey: resolveTypeKey(record, title)
  };
}

function defaultState() {
  return {
    list: [],
    loading: false,
    finished: false,
    pageNum: 1,
    pageSize: 20
  };
}

export const useInviteRewardStore = defineStore('invite-reward', {
  state: () => defaultState(),
  getters: {
    previewList: (state) => state.list.slice(0, 3)
  },
  actions: {
    reset() {
      Object.assign(this, defaultState());
    },
    async fetchNext({ address, reset = false } = {}) {
      if (reset) {
        this.reset();
      }

      if (this.loading || this.finished) {
        return this.list;
      }

      this.loading = true;
      try {
        const result = await ptAssetLogList({
          address: address || null,
          pageNum: this.pageNum,
          pageSize: this.pageSize
        });
        const rows = Array.isArray(result?.list)
          ? result.list.map((item) => normalizeInviteRewardRecord(item))
          : [];
        this.list = this.pageNum === 1 ? rows : [...this.list, ...rows];
        this.finished = rows.length < this.pageSize;
        if (rows.length > 0) {
          this.pageNum += 1;
        }
        return this.list;
      } finally {
        this.loading = false;
      }
    }
  }
});
